"use client"

import { useEffect, useRef } from "react"

/**
 * Preserves the user's reading position across the guide unlock flow.
 *
 * Two paths leak scroll position:
 *   1. Social OAuth → full page reload after callback → scrollY resets to 0.
 *   2. Email submit → no reload, but the gated section expands and the gate
 *      below it disappears, so the elements the user was looking at can shift.
 *
 * `markBeforeRedirect` is wired to SocialLoginButtons.onBeforeRedirect; on
 * mount we look for a recent save matching the current path and, once the
 * page has unlocked (isHydrated && isUnlocked), scroll back. Email unlock is
 * handled separately in the page via `restoreAfterInlineUnlock`.
 */

const KEY = "wgit_guide_scroll"
const TTL_MS = 10 * 60 * 1000 // 10 minutes — covers a slow OAuth round-trip

interface SavedPosition {
  path: string
  scrollY: number
  ts: number
}

function readSaved(): SavedPosition | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedPosition
    if (!parsed || typeof parsed.scrollY !== "number") return null
    if (Date.now() - parsed.ts > TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function clearSaved() {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

export function markBeforeRedirect() {
  if (typeof window === "undefined") return
  try {
    const data: SavedPosition = {
      path: window.location.pathname,
      scrollY: window.scrollY,
      ts: Date.now(),
    }
    sessionStorage.setItem(KEY, JSON.stringify(data))
  } catch {
    // sessionStorage may be unavailable (Safari private mode)
  }
}

/**
 * Restore scroll after OAuth round-trip. Waits until both hydration and
 * unlock have settled so the document is at its final height before we jump.
 */
export function useRestoreScrollAfterOAuth(opts: {
  isHydrated: boolean
  isUnlocked: boolean
  pathname: string
}) {
  const restoredRef = useRef(false)

  useEffect(() => {
    if (restoredRef.current) return
    if (!opts.isHydrated || !opts.isUnlocked) return

    const saved = readSaved()
    if (!saved) return
    if (saved.path !== opts.pathname) return

    restoredRef.current = true
    clearSaved()

    // Two rAFs: first lets React commit the unlocked tree, second waits for
    // browser layout so scrollHeight reflects the expanded content.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: saved.scrollY, behavior: "instant" as ScrollBehavior })
      })
    })
  }, [opts.isHydrated, opts.isUnlocked, opts.pathname])
}

/**
 * Capture the position of an anchor element in document coordinates *before*
 * inline unlock, then return a fn that re-anchors window.scrollY so the user
 * sees the same element in the same viewport offset after React re-renders.
 *
 * Use:
 *   const restore = captureAnchor(anchorEl)
 *   setIsUnlocked(true)
 *   // …after next paint
 *   restore()
 */
export function captureAnchor(anchorEl: HTMLElement | null): () => void {
  if (!anchorEl || typeof window === "undefined") return () => {}
  const docTopBefore = anchorEl.getBoundingClientRect().top + window.scrollY
  const viewportOffset = window.scrollY - docTopBefore // negative if anchor is below current scroll

  return () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const docTopAfter = anchorEl.getBoundingClientRect().top + window.scrollY
        window.scrollTo({
          top: docTopAfter + viewportOffset,
          behavior: "instant" as ScrollBehavior,
        })
      })
    })
  }
}
